import { toast } from "sonner";
import { connectionService } from "/app/src/services";
import { Connection } from "/app/src/models";
import { ConnectionState } from "../contexts/types";
//convert connections type and status `to an object with the keys as type, value as status

export function parseConnections(data: Connection[]): ConnectionState {
  ///https://stackoverflow.com/a/67042616
  const sqlConnection = data.find(
    (connection) => connection.type === "PowerPick SQL",
  );
  const webServicesConnection = data.find(
    (connection) => connection.type === "PowerPick Web Services",
  );
  return {
    powerPick: sqlConnection.status as 0 | 1,
    webServices: webServicesConnection.status as 0 | 1,
  };
}

/**
 * Tests a connection by invoking the connection service and updating the connection status.
 *
 * @param {function(boolean):void} setIsTesting - Function to update the testing status.
 * @param {Connection} connection - The connection object that includes details such as id, name, and status.
 * @param {function(Connection): Promise<any>} setConnection - Function to update the connection with a new status.
 * @returns {Promise<any>} Represents the asynchronous operation of testing a connection.
 */
export function testConnection(
  setIsTesting: (status: boolean) => void,
  connection: Connection,
  setConnection: (connection: Connection) => Promise<any>,
) {
  setIsTesting(true);

  const toastId = toast.loading(`Testing ${connection.name} connection`);

  return connectionService
    .testSingle(connection.id)
    .then((response) => {
      toast.dismiss(toastId);

      if ("connected" in response && response.connected) {
        if (connection.status === 0) {
          setConnection({ id: connection.id, status: 1 });
        }
        toast.success("Connection successful");
      } else {
        if (connection.status === 1) {
          setConnection({ id: connection.id, status: 0 });
        }
        if ("errors" in response) {
          toast.error(response.error);
        } else if ("statusCode" in response) {
          toast.error(`Connection failed - ${response.statusCode}`);
        } else {
          toast.error("Connection failed.");
        }
      }
    })
    .catch(() => {
      toast.dismiss(toastId);
      toast.error("Connection failed.");
      setConnection({ id: connection.id, status: 0 });
    })
    .finally(() => {
      setIsTesting(false);
    });
}
/**
 * Helper function to check if the connection status has changed
 * @param oldStatus old Connection Status
 * @param newStatus new Connection Status
 * @returns true if the connection status has changed
 */
export function checkStatusChange(
  oldStatus: ConnectionState,
  newStatus: ConnectionState,
) {
  return (
    oldStatus.powerPick !== newStatus.powerPick ||
    oldStatus.webServices !== newStatus.webServices
  );
}
